import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FileText, Search, Download, ChevronLeft, Clock, Plus, Edit2, Trash2, Eye, RefreshCw, MapPin, User, CreditCard, Building2, Mail, Settings } from "lucide-react";
import axios from "axios";
import { SiteSelector, CommercialProvider, useSite } from "./SiteSelector";

const API = '/api';

const AuditCentreContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedSite } = useSite();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [entityIdFilter, setEntityIdFilter] = useState(searchParams.get("entity_id") || "");
  const [actionFilter, setActionFilter] = useState(searchParams.get("action") || "all");
  const [entityTypeFilter, setEntityTypeFilter] = useState(searchParams.get("entity_type") || "all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const limit = 30;

  useEffect(() => { fetchLogs(); }, [page, actionFilter, entityTypeFilter, selectedSite, entityIdFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `${API}/commercial/audit?page=${page}&limit=${limit}`;
      if (actionFilter !== "all") url += `&action=${actionFilter}`;
      if (entityTypeFilter !== "all") url += `&entity_type=${entityTypeFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (selectedSite !== "all") url += `&site_id=${selectedSite}`;
      if (entityIdFilter) url += `&entity_id=${encodeURIComponent(entityIdFilter)}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      
      const res = await axios.get(url);
      setLogs(res.data.audit_logs || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    const headers = ["Audit ID", "Timestamp", "Action", "Entity Type", "Entity ID", "Site ID", "User ID", "Details"];
    const rows = logs.map(l => [l.audit_id, new Date(l.timestamp).toISOString(), l.action, l.entity_type, l.entity_id, l.site_id || "", l.user_id || "", JSON.stringify(l.details || {})]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "create": return <Plus className="w-4 h-4 text-green-500" />;
      case "update": return <Edit2 className="w-4 h-4 text-blue-500" />;
      case "delete": return <Trash2 className="w-4 h-4 text-red-500" />;
      case "view": return <Eye className="w-4 h-4 text-gray-500" />;
      case "email": return <Mail className="w-4 h-4 text-purple-500" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case "payment": return <CreditCard className="w-4 h-4 text-blue-500" />;
      case "retailer": return <Building2 className="w-4 h-4 text-teal-500" />;
      case "pass": return <User className="w-4 h-4 text-purple-500" />;
      case "site": return <MapPin className="w-4 h-4 text-green-500" />;
      case "settings": return <Settings className="w-4 h-4 text-gray-500" />;
      default: return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="commercial-audit-centre">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/commercial")} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <div><h1 className="text-xl font-bold text-gray-900">Audit Centre</h1><p className="text-sm text-gray-500">{total} audit entries (Commercial actions only)</p></div>
          </div>
          <div className="flex items-center gap-2">
            <SiteSelector />
            <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" /> Export</button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 max-w-xs relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchLogs()} placeholder="Search..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <select value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="all">All Actions</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="view">View</option>
            <option value="export">Export</option>
            <option value="email">Email</option>
            <option value="refund">Refund</option>
          </select>
          <select value={entityTypeFilter} onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="all">All Entities</option>
            <option value="payment">Payment</option>
            <option value="retailer">Retailer</option>
            <option value="pass">Pass</option>
            <option value="interest">Interest</option>
            <option value="email">Email</option>
            <option value="site">Site</option>
            <option value="settings">Settings</option>
          </select>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="From" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="To" />
          <button onClick={fetchLogs} className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Apply</button>
          {entityIdFilter && (<button onClick={() => { setEntityIdFilter(""); setPage(1); }} className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg">Clear Entity Filter</button>)}
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Entity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Entity ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Site</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (<tr><td colSpan={7} className="px-4 py-8 text-center"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" /></td></tr>)
              : logs.length === 0 ? (<tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No audit logs found</td></tr>)
              : logs.map((log) => (
                <tr key={log.audit_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-600">{new Date(log.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {getActionIcon(log.action)}
                      <span className={`px-2 py-1 text-xs font-medium rounded ${log.action === "create" ? "bg-green-100 text-green-700" : log.action === "update" ? "bg-blue-100 text-blue-700" : log.action === "delete" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>{log.action}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {getEntityIcon(log.entity_type)}
                      <span className="text-sm text-gray-700">{log.entity_type}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="font-mono text-xs text-gray-600">{log.entity_id}</span></td>
                  <td className="px-4 py-3">{log.site_id ? <span className="flex items-center gap-1 text-xs text-gray-500"><MapPin className="w-3 h-3" /></span> : <span className="text-xs text-gray-400">—</span>}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{log.user_id || "System"}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px] truncate">{log.details ? JSON.stringify(log.details).slice(0, 50) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (<div className="flex items-center justify-between mt-4"><p className="text-sm text-gray-500">Page {page} of {pages}</p><div className="flex items-center gap-2"><button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Previous</button><button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Next</button></div></div>)}
      </div>
    </div>
  );
};

export default function CommercialAuditCentre() {
  return (
    <CommercialProvider>
      <AuditCentreContent />
    </CommercialProvider>
  );
}
